import React, { useEffect, useState } from 'react';
import Header from '../Components/Header';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Col,
  Container,
  Row,
  Card,
  CardText,
  CardBody,
  CardHeader,
  Button,
  Form,
  FormControl,
  CardFooter,
  Spinner
} from 'react-bootstrap';
import { BASE_URL } from '../config';

export default function DoctorProfile() {
  const { id } = useParams(); // Extract id from URL params
  const [doctorList, setDoctorList] = useState([]); // Store doctor details
  const [detailId, setDetailId] = useState(''); // Store doctor details
  const [errorMessage, setErrorMessage] = useState(''); // Error message
  const [medicalDegreeFile, setMedicalDegreeFile] = useState(null); // Store medical degree file
  const [resumeFile, setResumeFile] = useState(null); // Store resume file
  const [licenseNumber, setLicenseNumber] = useState(''); // Store license number
  const [experience, setExperience] = useState(''); // Store experience
  const [consultationFee, setConsultationFee] = useState(''); // Store consultation fee
  const [currency, setCurrency] = useState(''); // Store consultation fee
  const [instituteName, setInstituteName] = useState(''); // Store institute name
  const [instituteCountry, setInstituteCountry] = useState(''); // Store institute country
  const [isEditing, setIsEditing] = useState(true); // Initially in edit mode for both insert and update
  const [loading, setLoading] = useState(false); // Loading state
  const navigate = useNavigate(); // Navigate between pages

  // Fetch doctor profile data when component mounts or id changes
  useEffect(() => {
    if (id) {
      const fetchDoctorProfile = async () => {
        setLoading(true); // Start loading before the API call
        try {
          const response = await fetch(`${BASE_URL}DoctorDetailInfo?id=${id}`, {
            method: 'POST',
            headers: {
              'Accept': 'application/json',
            },
          });

          const data = await response.json();
          if (response.ok && data.doctorList && data.doctorList.length > 0) {
            setDoctorList(data.doctorList);
            const doctor = data.doctorList[0];
            setLicenseNumber(doctor.licenseNo || '');
            setDetailId(doctor.detailId || '');
            setConsultationFee(doctor.consultationFee || '');
            setCurrency(doctor.currency || 'PKR');
            setInstituteName(doctor.instituteName || '');
            setInstituteCountry(doctor.instituteCountry || '');
            setExperience(doctor.experience || '');


            setIsEditing(true); // Edit mode
          } else {
            setErrorMessage('No Details found.');
          }
        } catch (error) {
          console.error('Error fetching doctor profile:', error);
          setErrorMessage('Please Update the Details');
        } finally {
          setLoading(false); // Stop loading after the API call is complete
        }
      };

      fetchDoctorProfile();
    } else {
      setIsEditing(true); // Insert mode
    }
  }, [id]);


  // Handle file uploads
  const handleUpload = (e) => {
    if (e.target.name === 'medicalDegree') {
      setMedicalDegreeFile(e.target.files[0]);
    } else if (e.target.name === 'resume') {
      setResumeFile(e.target.files[0]);
    }
  };

  // Save the changes
  const handleSave = async () => {
    const formData = new FormData();
    formData.append('Docid', id || '');
    formData.append('ConsultationFee', consultationFee);
    formData.append('Currency', currency);
    formData.append('Resume', resumeFile);
    formData.append('MedicalDegree', medicalDegreeFile);
    formData.append('LicenseNo', licenseNumber);
    formData.append('InstituteName', instituteName);
    formData.append('InstituteCountry', instituteCountry);
    formData.append('Experience', experience + ' Years'); // Ensure experience is a string

    try {
      const response = await fetch(`${BASE_URL}DoctorDetailEdit`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();
      if (data.responseCode === '0000') {
        alert(id ? 'Doctor Details Successfully Updated' : 'Doctor Details Successfully Added');
        navigate(`/DoctorProfile/${id || data.newId}`); // Navigate after success
        setIsEditing(false); // Switch to view mode after saving
      } else {
        alert('Failed to save doctor details');
      }
    } catch (error) {
      console.error('Error saving doctor details:', error);
      alert('There was an error saving the doctor details');
    }
  };

  // Reset changes if cancel is clicked
  const handleCancel = () => {
    setIsEditing(false); // Reset to view mode
    navigate(`/DoctorProfile/${id || ''}`); // Go back to the original profile view
  };

  // alert(detailId);
  return (
    <Container fluid className='py-0'>
      <Header />
      <Row className='d-flex justify-content-center align-items-center'>
        <Col xs={12} sm={12} md={8} lg={6} className='m-5'>
          <Card className='shadow-sm'>
            <CardHeader className='text-center bg_primary text-white'>
              <h4>{detailId ? 'Edit Doctor Profile' : 'Insert Doctor Profile'}</h4>
            </CardHeader>
            <CardBody>
              {loading ? (
                <div className="text-center">
                  <Spinner animation="border" variant="primary" />
                  <p>Loading...</p>
                </div>
              ) : (
                <Form>
                  <Row>
                    <Col xs={6} sm={6} md={6} lg={6}>
                      <Form.Group>
                        <Form.Label>License No</Form.Label>
                        <FormControl
                          type="text"
                          placeholder="Enter License Number"
                          value={licenseNumber}
                          onChange={(e) => setLicenseNumber(e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={6} sm={6} md={6} lg={6}>
                      <Form.Group>
                        <Form.Label>Experience in years</Form.Label>
                        <FormControl
                          type="number"
                          placeholder="Enter Experience"
                          value={experience.replace(/\D/g, '')}
                          onChange={(e) => setExperience(e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <hr />
                  <Row>
                    <Col xs={6} sm={6} md={6} lg={6}>
                      <Form.Group>
                        <Form.Label>Consultation Fee</Form.Label>
                        <FormControl
                          type="text"
                          placeholder="Enter Consultation Fee"
                          value={consultationFee}
                          onChange={(e) => setConsultationFee(e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={6} sm={6} md={6} lg={6}>
                      <Form.Group>
                        <Form.Label>Select Currency</Form.Label>
                        <Form.Control
                          as="select"
                          name="feeType"
                          value={currency}
                          onChange={(e) => setCurrency(e.target.value)}
                        >
                          {/* <option value="">Select Currency</option> */}
                          <option value="PKR">PKR</option>
                          <option value="USD">USD</option>
                          {/* <option value="EUR">EUR</option>
                          <option value="INR">INR</option> */}
                        </Form.Control>
                      </Form.Group>
                    </Col>
                  </Row>
                  <hr />
                  <Row>
                    <Col xs={12} sm={12} md={6} lg={6}>
                      <Form.Group>
                        <Form.Label>Medical Institute</Form.Label>
                        <FormControl
                          type="text"
                          placeholder="Enter Medical Institute"
                          value={instituteName}
                          onChange={(e) => setInstituteName(e.target.value)}
                        />
                      </Form.Group>
                    </Col>

                    <Col xs={12} sm={12} md={6} lg={6}>
                      <Form.Group>
                        <Form.Label>Institute Country</Form.Label>
                        <FormControl
                          type="text"
                          placeholder="Enter Institute Country"
                          value={instituteCountry}
                          onChange={(e) => setInstituteCountry(e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <hr />
                  <Row>
                    <Col xs={12} sm={12} md={6} lg={6}>
                      <Form.Group>
                        <Form.Label>Medical Degree</Form.Label>
                        <FormControl
                          type="file"
                          name="medicalDegree"
                          onChange={handleUpload}
                        />
                      </Form.Group>
                    </Col>

                    <Col xs={12} sm={12} md={6} lg={6}>
                      <Form.Group>
                        <Form.Label>Resume</Form.Label>
                        <FormControl
                          type="file"
                          name="resume"
                          onChange={handleUpload}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Form>
              )}
            </CardBody>
            <CardFooter>
              <Row>
                <Col xs={5} sm={6} md={6} lg={6}>
                  <Button size="lg" className="w-100" variant="danger" onClick={handleCancel}>
                    Cancel
                  </Button>
                </Col>
                <Col xs={7} sm={6} md={6} lg={6}>
                  <Button size="lg" className="w-100 btn_primary" variant="primary" onClick={handleSave}>
                    {detailId ? 'Save Changes' : 'Update Changes'}
                  </Button>
                </Col>
              </Row>
            </CardFooter>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
